import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CheckIcon from 'images/v5/icons/check.svg';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { addStatus, selectStatusById, updateStatus } from 'store/parameters/statusesSlice';
import { getStatusColorTranslationKey, STATUS_COLORS } from 'utils/statusColors';
import styles from './StatusEditor.module.scss';
import useStatusValidation from './useStatusValidation';
function StatusEditor({ create = false }) {
    var _a;
    const dispatch = useDispatch();
    const statusId = (_a = useSelector(selectModalByName(create ? 'statusCreator' : 'statusEditor'))) === null || _a === void 0 ? void 0 : _a.id;
    const statusDetails = useSelector((state) => selectStatusById(state, statusId || '')) || null;
    const { t } = useTranslation(['specification', 'common']);
    const [formValues, setFormValues] = useState({
        namePL: '',
        nameEN: '',
        color: '',
    });
    const { errors, isValid, pendingFields, markTouched, validateAll } = useStatusValidation(formValues, t);
    useEffect(() => {
        if (!create && statusDetails) {
            setFormValues({
                namePL: statusDetails.namePL,
                nameEN: statusDetails.nameEN,
                color: statusDetails.color,
            });
        }
    }, [create, statusDetails, statusId]);
    const handleFormChange = ({ target: { name, value }, }) => {
        setFormValues((values) => (Object.assign(Object.assign({}, values), { [name]: value })));
    };
    const handleColorSelect = (color) => {
        setFormValues((values) => (Object.assign(Object.assign({}, values), { color })));
        markTouched('color');
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: create ? 'statusCreator' : 'statusEditor' }));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        if (create) {
            dispatch(addStatus({
                namePL: formValues.namePL,
                nameEN: formValues.nameEN,
                color: formValues.color,
            }));
        }
        else {
            dispatch(updateStatus({
                statusId: statusDetails.id,
                namePL: formValues.namePL,
                nameEN: formValues.nameEN,
                color: formValues.color,
            }));
        }
        dispatch(closeModal({ name: create ? 'statusCreator' : 'statusEditor' }));
    };
    return (React.createElement(Modal, { name: create ? 'statusCreator' : 'statusEditor', size: "m", header: React.createElement(Label, { weight: "strong" }, create ? t('add_status') : t('edit_status')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: false, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCancel, validateAll: validateAll }), onClose: handleCancel },
        React.createElement(Grid, null,
            React.createElement(FormField, { direction: "row", label: t('status_name_pl'), error: errors.namePL },
                React.createElement(Input, { value: formValues === null || formValues === void 0 ? void 0 : formValues.namePL, onChange: handleFormChange, onBlur: () => markTouched('namePL'), name: "namePL", error: Boolean(errors.namePL), autoFocus: true })),
            React.createElement(FormField, { direction: "row", label: t('status_name_en'), error: errors.nameEN },
                React.createElement(Input, { value: formValues === null || formValues === void 0 ? void 0 : formValues.nameEN, onChange: handleFormChange, onBlur: () => markTouched('nameEN'), name: "nameEN", error: Boolean(errors.nameEN) })),
            React.createElement(FormField, { direction: "row", label: t('status_color'), error: errors.color },
                React.createElement("div", { className: styles.colors }, STATUS_COLORS.map((color) => (React.createElement("button", { key: color, type: "button", className: styles.swatch, style: { ['--swatch-color']: `var(--status-color-${color.toLowerCase()})` }, title: t(getStatusColorTranslationKey(color)), onClick: () => handleColorSelect(color) },
                    React.createElement("span", { className: styles.fill }),
                    formValues.color === color && React.createElement(CheckIcon, { className: styles.check })))))))));
}
export default memo(StatusEditor);
